import React, { useState, useEffect } from 'react';
import { employeesApi, officesApi } from '../lib/api';
import { Users, Plus, Edit, Phone, Mail, Building2, Shield, Hash } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const EmployeesPage = () => {
  const [employees, setEmployees] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', office_id: '', is_loan_officer: false, mobile_number: '', email: '', joining_date: '', position: '', department: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [empRes, offRes] = await Promise.all([employeesApi.list(), officesApi.list()]);
      setEmployees(empRes.data);
      setOffices(offRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.first_name || !form.last_name) return toast.error('First and last name are required');
    try {
      const res = await employeesApi.create(form);
      toast.success(`Employee created: ${res.data.employee_number}`);
      setShowModal(false);
      setForm({ first_name: '', last_name: '', office_id: '', is_loan_officer: false, mobile_number: '', email: '', joining_date: '', position: '', department: '' });
      load();
    } catch (e) { toast.error('Failed to create employee'); }
  };

  const getOfficeName = (id) => offices.find(o => o.id === id)?.name || '-';

  return (
    <div className="space-y-6 fade-in" data-testid="employees-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Admin</span><span>/</span><span>Organization</span><span>/</span><span className="text-[#1f1f1f] font-medium">Manage Employees</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">STAFF MANAGEMENT</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Manage Employees</h1>
          <p className="text-xs text-gray-500 mt-1">HR records for all staff. System access is granted separately under Admin &gt; Users.</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="add-employee-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Employee
        </Button>
      </div>

      <div className="bg-white border border-gray-200">
        <table className="w-full" data-testid="employees-table">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Employee ID</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Name</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Position</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Office</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Contact</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Loan Officer</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">System Access</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            employees.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-gray-400">No employees found. Click "Add Employee" to create one.</td></tr> :
            employees.map(emp => (
              <tr key={emp.id} className="border-b hover:bg-gray-50" data-testid={`employee-row-${emp.id}`}>
                <td className="px-4 py-3">
                  <span className="font-mono text-xs bg-gray-100 text-[#1f1f1f] px-2 py-1" data-testid={`emp-number-${emp.id}`}>
                    {emp.employee_number || '-'}
                  </span>
                </td>
                <td className="px-4 py-3"><div className="font-medium text-sm text-[#1f1f1f]">{emp.first_name} {emp.last_name}</div></td>
                <td className="px-4 py-3 text-sm text-gray-600">{emp.position || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{getOfficeName(emp.office_id)}</td>
                <td className="px-4 py-3">
                  <div className="text-xs text-gray-500 space-y-0.5">
                    {emp.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{emp.email}</div>}
                    {emp.mobile_number && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{emp.mobile_number}</div>}
                    {!emp.email && !emp.mobile_number && '-'}
                  </div>
                </td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 ${emp.is_loan_officer ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{emp.is_loan_officer ? 'Yes' : 'No'}</span></td>
                <td className="px-4 py-3 text-center">
                  {emp.has_system_access ? (
                    <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 inline-flex items-center gap-1"><Shield className="w-3 h-3" />Linked</span>
                  ) : (
                    <span className="text-xs text-gray-400">None</span>
                  )}
                </td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-1 bg-green-50 text-green-700">{emp.status || 'Active'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" aria-describedby="employee-form-desc">
          <DialogHeader><DialogTitle>Add Employee</DialogTitle></DialogHeader>
          <p id="employee-form-desc" className="sr-only">Form to add a new employee</p>
          <p className="text-xs text-gray-500 mb-2">A unique Employee ID (EMP-YYYYMMDD-XXXX) will be auto-generated.</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>First Name *</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} data-testid="emp-first-name" /></div>
              <div><Label>Last Name *</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} data-testid="emp-last-name" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Position</Label><Input value={form.position} onChange={e => setForm({...form, position: e.target.value})} placeholder="e.g., Teller, Loan Officer, Gardener" data-testid="emp-position" /></div>
              <div><Label>Department</Label><Input value={form.department} onChange={e => setForm({...form, department: e.target.value})} placeholder="e.g., Operations, HR, IT" data-testid="emp-department" /></div>
            </div>
            <div><Label>Office / Branch *</Label>
              <Select value={form.office_id} onValueChange={v => setForm({...form, office_id: v})}>
                <SelectTrigger data-testid="emp-office"><SelectValue placeholder="Select Office" /></SelectTrigger>
                <SelectContent>{offices.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Mobile</Label><Input value={form.mobile_number} onChange={e => setForm({...form, mobile_number: e.target.value})} data-testid="emp-mobile" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="emp-email" /></div>
            </div>
            <div><Label>Joining Date</Label><Input type="date" value={form.joining_date} onChange={e => setForm({...form, joining_date: e.target.value})} data-testid="emp-join-date" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_loan_officer} onCheckedChange={v => setForm({...form, is_loan_officer: v})} data-testid="emp-loan-officer" /><Label>Is Loan Officer</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="emp-submit">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesPage;
